'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { SendIcon, ShieldCheckIcon, ShieldXIcon, SquarePenIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { UserAvatar } from '@/components/partials';
import ProfileContentSkeleton from './profile-content-skeleton';
import UpdateProfileDialog from './update-profile-dialog';
import { formatLongDateTime } from '@/lib/utils';
import { useAppLocale, useSession } from '@/hooks';
import { resendEmailVerification } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
const ProfilePageContent = () => {
  const $ = _c(164);
  const t = useTranslations();
  const {
    backendLocale
  } = useAppLocale();
  const {
    user,
    sessionIsLoading,
    sessionError,
    resetSession
  } = useSession();
  const [dialogOpen, setDialogOpen] = useState(false);
  let t0;
  if ($[0] !== t) {
    t0 = {
      mutationFn: resendEmailVerification,
      onSuccess: () => {
        toast.success(t("verification_email_sent"));
      },
      onError: error => {
        if (error.response?.data?.message === "email_already_verified") {
          toast.info(t("email_already_verified"));
        } else {
          toast.error(getErrorMessage(error, t("something_went_wrong")));
        }
      }
    };
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    mutate: sendVerification,
    isPending: sendingVerification
  } = useMutation(t0);
  if (sessionIsLoading) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <ProfileContentSkeleton />;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (sessionError) {
    let t1;
    if ($[3] !== t) {
      t1 = t("something_went_wrong");
      $[3] = t;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== t1) {
      t2 = <span className="text-destructive font-semibold">{t1}</span>;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== t) {
      t3 = t("try_again");
      $[7] = t;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== resetSession || $[10] !== t3) {
      t4 = <Button onClick={resetSession}>{t3}</Button>;
      $[9] = resetSession;
      $[10] = t3;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] !== t2 || $[13] !== t4) {
      t5 = <div className="flex h-full flex-col items-center justify-center gap-4">{t2}{t4}</div>;
      $[12] = t2;
      $[13] = t4;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    return t5;
  }
  if (!user) {
    return null;
  }
  let t1;
  if ($[15] !== dialogOpen || $[16] !== user) {
    t1 = user && <UpdateProfileDialog user={user} open={dialogOpen} onOpenChange={setDialogOpen} />;
    $[15] = dialogOpen;
    $[16] = user;
    $[17] = t1;
  } else {
    t1 = $[17];
  }
  let t2;
  if ($[18] !== user) {
    t2 = <UserAvatar user={user} size="lg" />;
    $[18] = user;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  let t3;
  if ($[20] !== user.fullName) {
    t3 = <span className="text-lg font-semibold text-gray-900">{user.fullName}</span>;
    $[20] = user.fullName;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== user.email) {
    t4 = <span className="text-muted-foreground text-sm">{user.email}</span>;
    $[22] = user.email;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== t3 || $[25] !== t4) {
    t5 = <div className="flex flex-col">{t3}{t4}</div>;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== t2 || $[28] !== t5) {
    t6 = <div className="flex items-center gap-4">{t2}{t5}</div>;
    $[27] = t2;
    $[28] = t5;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  let t8;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setDialogOpen(true);
    t8 = <SquarePenIcon className="h-4 w-4" />;
    $[30] = t7;
    $[31] = t8;
  } else {
    t7 = $[30];
    t8 = $[31];
  }
  let t9;
  if ($[32] !== t) {
    t9 = t("update_profile");
    $[32] = t;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== t9) {
    t10 = <div className="flex flex-row items-center justify-end gap-2"><Button onClick={t7}>{t8}{t9}</Button></div>;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== t10 || $[37] !== t6) {
    t11 = <div className="grid grid-cols-1 items-center justify-between gap-4 rounded-lg border border-gray-100 p-4 md:grid-cols-2">{t6}{t10}</div>;
    $[36] = t10;
    $[37] = t6;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== t) {
    t12 = t("firstname");
    $[39] = t;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t12) {
    t13 = <span className="text-muted-foreground text-xs font-medium">{t12}</span>;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== user.firstname) {
    t14 = <span className="text-sm font-semibold text-gray-900">{user.firstname}</span>;
    $[43] = user.firstname;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t13 || $[46] !== t14) {
    t15 = <div className="flex flex-col gap-1 rounded-md border bg-gray-50 p-4">{t13}{t14}</div>;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t) {
    t16 = t("firstname");
    $[48] = t;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== t16) {
    t17 = <span className="text-muted-foreground text-xs font-medium">{t16}</span>;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== user.lastname) {
    t18 = <span className="text-sm font-semibold text-gray-900">{user.lastname}</span>;
    $[52] = user.lastname;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== t17 || $[55] !== t18) {
    t19 = <div className="flex flex-col gap-1 rounded-md border bg-gray-50 p-4">{t17}{t18}</div>;
    $[54] = t17;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t) {
    t20 = t("email");
    $[57] = t;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== t20) {
    t21 = <span className="text-muted-foreground text-xs font-medium">{t20}</span>;
    $[59] = t20;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== backendLocale || $[62] !== sendVerification || $[63] !== sendingVerification || $[64] !== t || $[65] !== user.emailVerifiedAt) {
    t22 = user.emailVerifiedAt ? <div className="text-success flex flex-row items-center gap-1 text-xs"><ShieldCheckIcon className="h-4 w-4" />{t("verified_on_date", {
        date: formatLongDateTime(user.emailVerifiedAt, backendLocale)
      })}</div> : <div className="flex flex-row items-center gap-3 text-xs"><div className="text-destructive flex flex-row items-center gap-1"><ShieldXIcon className="h-4 w-4" />{t("not_verified")}</div><Button size="sm" variant="warning" onClick={() => sendVerification()} loading={sendingVerification} disabled={sendingVerification}>{t("send_verification_email")}<SendIcon className="h-3! w-3!" /></Button></div>;
    $[61] = backendLocale;
    $[62] = sendVerification;
    $[63] = sendingVerification;
    $[64] = t;
    $[65] = user.emailVerifiedAt;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== t22 || $[68] !== user.email) {
    t23 = <span className="flex flex-col items-start gap-1 text-sm font-semibold text-gray-900">{user.email}{t22}</span>;
    $[67] = t22;
    $[68] = user.email;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== t21 || $[71] !== t23) {
    t24 = <div className="flex flex-col gap-1 rounded-md border bg-gray-50 p-4">{t21}{t23}</div>;
    $[70] = t21;
    $[71] = t23;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  if ($[73] !== t) {
    t25 = t("phone");
    $[73] = t;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== t25) {
    t26 = <span className="text-muted-foreground text-xs font-medium">{t25}</span>;
    $[75] = t25;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  let t27;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <span className="text-sm font-semibold text-gray-900">-</span>;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  let t28;
  if ($[78] !== t26) {
    t28 = <div className="flex flex-col gap-1 rounded-md border bg-gray-50 p-4">{t26}{t27}</div>;
    $[78] = t26;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] !== t) {
    t29 = t("gender");
    $[80] = t;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== t29) {
    t30 = <span className="text-muted-foreground text-xs font-medium">{t29}</span>;
    $[82] = t29;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  let t31;
  if ($[84] !== t || $[85] !== user.gender) {
    t31 = t(user.gender);
    $[84] = t;
    $[85] = user.gender;
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  let t32;
  if ($[87] !== t31) {
    t32 = <span className="text-sm font-semibold text-gray-900">{t31}</span>;
    $[87] = t31;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  let t33;
  if ($[89] !== t30 || $[90] !== t32) {
    t33 = <div className="flex flex-col gap-1 rounded-md border bg-gray-50 p-4">{t30}{t32}</div>;
    $[89] = t30;
    $[90] = t32;
    $[91] = t33;
  } else {
    t33 = $[91];
  }
  let t34;
  if ($[92] !== t) {
    t34 = t("timezone");
    $[92] = t;
    $[93] = t34;
  } else {
    t34 = $[93];
  }
  let t35;
  if ($[94] !== t34) {
    t35 = <span className="text-muted-foreground text-xs font-medium">{t34}</span>;
    $[94] = t34;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  let t36;
  if ($[96] !== user.timezone) {
    t36 = <span className="text-sm font-semibold text-gray-900">{user.timezone}</span>;
    $[96] = user.timezone;
    $[97] = t36;
  } else {
    t36 = $[97];
  }
  let t37;
  if ($[98] !== t35 || $[99] !== t36) {
    t37 = <div className="flex flex-col gap-1 rounded-md border bg-gray-50 p-4">{t35}{t36}</div>;
    $[98] = t35;
    $[99] = t36;
    $[100] = t37;
  } else {
    t37 = $[100];
  }
  let t38;
  if ($[101] !== t) {
    t38 = t("currency");
    $[101] = t;
    $[102] = t38;
  } else {
    t38 = $[102];
  }
  let t39;
  if ($[103] !== t38) {
    t39 = <span className="text-muted-foreground text-xs font-medium">{t38}</span>;
    $[103] = t38;
    $[104] = t39;
  } else {
    t39 = $[104];
  }
  const t40 = user.currencyCode ?? "-";
  let t41;
  if ($[105] !== t40) {
    t41 = <span className="text-sm font-semibold text-gray-900">{t40}</span>;
    $[105] = t40;
    $[106] = t41;
  } else {
    t41 = $[106];
  }
  let t42;
  if ($[107] !== t39 || $[108] !== t41) {
    t42 = <div className="flex flex-col gap-1 rounded-md border bg-gray-50 p-4">{t39}{t41}</div>;
    $[107] = t39;
    $[108] = t41;
    $[109] = t42;
  } else {
    t42 = $[109];
  }
  let t43;
  if ($[110] !== t15 || $[111] !== t19 || $[112] !== t24 || $[113] !== t28 || $[114] !== t33 || $[115] !== t37 || $[116] !== t42) {
    t43 = <div className="grid grid-cols-1 gap-4 md:grid-cols-3">{t15}{t19}{t24}{t28}{t33}{t37}{t42}</div>;
    $[110] = t15;
    $[111] = t19;
    $[112] = t24;
    $[113] = t28;
    $[114] = t33;
    $[115] = t37;
    $[116] = t42;
    $[117] = t43;
  } else {
    t43 = $[117];
  }
  let t44;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <Separator />;
    $[118] = t44;
  } else {
    t44 = $[118];
  }
  let t45;
  if ($[119] !== t) {
    t45 = t("account_approval_date");
    $[119] = t;
    $[120] = t45;
  } else {
    t45 = $[120];
  }
  let t46;
  if ($[121] !== t45) {
    t46 = <span className="text-muted-foreground text-xs font-medium">{t45}</span>;
    $[121] = t45;
    $[122] = t46;
  } else {
    t46 = $[122];
  }
  let t47;
  if ($[123] !== backendLocale || $[124] !== user.approvedAt) {
    t47 = formatLongDateTime(user.approvedAt, backendLocale);
    $[123] = backendLocale;
    $[124] = user.approvedAt;
    $[125] = t47;
  } else {
    t47 = $[125];
  }
  let t48;
  if ($[126] !== t47) {
    t48 = <span className="text-sm font-semibold text-gray-900">{t47}</span>;
    $[126] = t47;
    $[127] = t48;
  } else {
    t48 = $[127];
  }
  let t49;
  if ($[128] !== t46 || $[129] !== t48) {
    t49 = <div className="flex flex-col gap-1 rounded-md border bg-gray-50 p-4">{t46}{t48}</div>;
    $[128] = t46;
    $[129] = t48;
    $[130] = t49;
  } else {
    t49 = $[130];
  }
  let t50;
  if ($[131] !== t) {
    t50 = t("created_at");
    $[131] = t;
    $[132] = t50;
  } else {
    t50 = $[132];
  }
  let t51;
  if ($[133] !== t50) {
    t51 = <span className="text-muted-foreground text-xs font-medium">{t50}</span>;
    $[133] = t50;
    $[134] = t51;
  } else {
    t51 = $[134];
  }
  let t52;
  if ($[135] !== backendLocale || $[136] !== user.createdAt) {
    t52 = formatLongDateTime(user.createdAt, backendLocale);
    $[135] = backendLocale;
    $[136] = user.createdAt;
    $[137] = t52;
  } else {
    t52 = $[137];
  }
  let t53;
  if ($[138] !== t52) {
    t53 = <span className="text-sm font-semibold text-gray-900">{t52}</span>;
    $[138] = t52;
    $[139] = t53;
  } else {
    t53 = $[139];
  }
  let t54;
  if ($[140] !== t51 || $[141] !== t53) {
    t54 = <div className="flex flex-col gap-1 rounded-md border bg-gray-50 p-4">{t51}{t53}</div>;
    $[140] = t51;
    $[141] = t53;
    $[142] = t54;
  } else {
    t54 = $[142];
  }
  let t55;
  if ($[143] !== t) {
    t55 = t("updated_at");
    $[143] = t;
    $[144] = t55;
  } else {
    t55 = $[144];
  }
  let t56;
  if ($[145] !== t55) {
    t56 = <span className="text-muted-foreground text-xs font-medium">{t55}</span>;
    $[145] = t55;
    $[146] = t56;
  } else {
    t56 = $[146];
  }
  let t57;
  if ($[147] !== backendLocale || $[148] !== user.updatedAt) {
    t57 = formatLongDateTime(user.updatedAt, backendLocale);
    $[147] = backendLocale;
    $[148] = user.updatedAt;
    $[149] = t57;
  } else {
    t57 = $[149];
  }
  let t58;
  if ($[150] !== t57) {
    t58 = <span className="text-sm font-semibold text-gray-900">{t57}</span>;
    $[150] = t57;
    $[151] = t58;
  } else {
    t58 = $[151];
  }
  let t59;
  if ($[152] !== t56 || $[153] !== t58) {
    t59 = <div className="flex flex-col gap-1 rounded-md border bg-gray-50 p-4">{t56}{t58}</div>;
    $[152] = t56;
    $[153] = t58;
    $[154] = t59;
  } else {
    t59 = $[154];
  }
  let t60;
  if ($[155] !== t49 || $[156] !== t54 || $[157] !== t59) {
    t60 = <div className="grid grid-cols-1 gap-4 md:grid-cols-3">{t49}{t54}{t59}</div>;
    $[155] = t49;
    $[156] = t54;
    $[157] = t59;
    $[158] = t60;
  } else {
    t60 = $[158];
  }
  let t61;
  if ($[159] !== t1 || $[160] !== t11 || $[161] !== t43 || $[162] !== t60) {
    t61 = <div className="flex w-full flex-col gap-6 rounded-lg bg-white p-6 shadow-sm">{t1}{t11}{t43}{t44}{t60}</div>;
    $[159] = t1;
    $[160] = t11;
    $[161] = t43;
    $[162] = t60;
    $[163] = t61;
  } else {
    t61 = $[163];
  }
  return t61;
};
export default ProfilePageContent;